import React, { useContext, useEffect, useState } from "react";
import "./panvel.css";
import { GlobalContext } from "../../App";
import { HOST, HOST_MEDIA_URL } from "../host";
import L from "leaflet";

export default function PanvelSurvey() {
  const { map } = useContext(GlobalContext);
  const [panvelLayer, setPanvelLayer] = useState(null);
  const [tableData, setTableData] = useState([]);

  const addPanvelLayer = async (e) => {
    if (!map) return;

    let globalEditMode = false;

    const isChecked = e.target.checked;
    const layerName = e.target.value;

    // If unchecked, remove from map and stop here
    if (!isChecked) {
      if (panvelLayer) {
        map.removeLayer(panvelLayer);
        setPanvelLayer(null);
      }
      return;
    }

    // Remove previous layer if any
    if (panvelLayer) {
      map.removeLayer(panvelLayer);
    }

    // Uncheck all other checkboxes in same group
    document.querySelectorAll(".check-map").forEach((input) => {
      if (input !== e.target) input.checked = false;
    });

    // Build full URL with cache-busting parameter
    const url = `${HOST_MEDIA_URL}/media/${layerName}?t=${Date.now()}`;
    console.log("Fetching GeoJSON from:", url);

    try {
      const response = await fetch(url, { cache: "no-store" });
      if (!response.ok) throw new Error("Failed to fetch GeoJSON");

      const data = await response.json();
      console.log("GeoJSON data loaded:", data);
      const features = data.features.map((f) => f.properties);
      setTableData(features);

      // Create GeoJSON layer
      const layer = L.geoJSON(data, {
        style: (feature) => {
          // Initial color based on survey status
          if (feature.properties.sur_sts === true)
            return { color: "green", weight: 8, opacity: 0.9 };
          return { color: "black", weight: 8, opacity: 0.9 };
        },
        onEachFeature: (feature, layer) => {
          const props = feature.properties;

          // Highlight yellow when popup opens
          layer.on("popupopen", () => {
            layer.setStyle({ color: "yellow" });
          });

          // Popup structure
          const popupDiv = document.createElement("div");
          popupDiv.style.width = "320px";
          popupDiv.style.maxHeight = "350px";
          popupDiv.style.overflow = "auto";

          const headerDiv = document.createElement("div");
          headerDiv.style.display = "flex";
          headerDiv.style.alignItems = "center";
          headerDiv.style.marginBottom = "8px";
          headerDiv.style.gap = "8px";
          headerDiv.innerHTML = `
           <button id="editSaveBtn" style="
              background-color:#2b7a2b;
              color:white;
              border:none;
              padding:4px 10px;
              border-radius:5px;
              cursor:pointer;
              font-size:13px;
            ">Edit</button>
          </div>
        `;
          popupDiv.appendChild(headerDiv);

          // Create table
          const table = document.createElement("table");
          table.style.borderCollapse = "collapse";
          table.style.width = "100%";
          table.style.fontSize = "13px";

          const thead = document.createElement("thead");
          thead.innerHTML = `
        <tr style="background:#2b7a2b; color:#fff;">
          <th style="padding:4px; border:1px solid #ccc;">Attribute</th>
          <th style="padding:4px; border:1px solid #ccc;">Value</th>
        </tr>`;
          table.appendChild(thead);

          const mandatoryKeys = [
            "Rd_Name",
            "Cons_Mat",
            "CW_Width",
            "FP_Width",
            "FP_Cons_Ma",
          ];
          const tbody = document.createElement("tbody");

          Object.entries(props).forEach(([key, val]) => {
            //  Skip adding 'sur_sts' visually (but keep it in props)
            if (
              [
                "sur_sts",
                "OBJECTID_1",
                "OBJECTID_2",
                "Join_Count",
                "TARGET_FID",
                "OBJECTID",
                "Join_Cou_1",
                "TARGET_F_1",
                "osm_id",
                "name",
                "Length_km",
                "Ward_No",
                "Maintain",
                "villageNam",
                "Row_Width",
              ].includes(key)
            )
              return;

            const tr = document.createElement("tr");
            let inputElement;

            if (key === "Road_Qua" || key === "Road_Qual") {
              inputElement = document.createElement("select");
              ["Poor", "Very Poor", "Ok", "Good"].forEach((optVal) => {
                const option = document.createElement("option");
                option.value = optVal;
                option.text = optVal;
                if (val === optVal) option.selected = true;
                inputElement.appendChild(option);
              });
            } else if (key === "Encroach") {
              inputElement = document.createElement("select");
              ["Yes", "No", "Major Encroach"].forEach((optVal) => {
                const option = document.createElement("option");
                option.value = optVal;
                option.text = optVal;
                if (val === optVal) option.selected = true;
                inputElement.appendChild(option);
              });
            } else if (key === "StreetLigh" || key === "Street_Lig") {
              inputElement = document.createElement("select");
              ["Yes", "No"].forEach((optVal) => {
                const option = document.createElement("option");
                option.value = optVal;
                option.text = optVal;
                if (val === optVal) option.selected = true;
                inputElement.appendChild(option);
              });
            } 
            else if (key === "Foot_Path") {
              inputElement = document.createElement("select");
              ["Yes", "No", "Other"].forEach((optVal) => {
                const option = document.createElement("option");
                option.value = optVal;
                option.text = optVal;
                if (val === optVal) option.selected = true;
                inputElement.appendChild(option);
              });
            } 
            else if (key === "FP_Cons_Ma") {
              inputElement = document.createElement("select");
              [
                "Concrete (PCC)",
                "Stone Slabs ",
                "Paver block ",
                "Bitumen ",
                "Other",
              ].forEach((optVal) => {
                const option = document.createElement("option");
                option.value = optVal;
                option.text = optVal;
                if (val === optVal) option.selected = true;
                inputElement.appendChild(option);
              });
            } else if (key === "Cons_Mat") {
              inputElement = document.createElement("select");
              [
                "Bituminous",
                "Concrete ",
                "Gravel",
                "Paver Block",
                "WBM (Water Bound Macadam)",
                "Other",
              ].forEach((optVal) => {
                const option = document.createElement("option");
                option.value = optVal;
                option.text = optVal;
                if (val === optVal) option.selected = true;
                inputElement.appendChild(option);
              });
            } else if (key === "Sub_Class") {
              inputElement = document.createElement("select");
              [
                "Minor  Road",
                "Major  Road",
                "State highway",
                "National highway",
                "Kaccha road",
                "Service Road",
                "Village road",
                "Other",
              ].forEach((optVal) => {
                const option = document.createElement("option");
                option.value = optVal;
                option.text = optVal;
                if (val === optVal) option.selected = true;
                inputElement.appendChild(option);
              });
            } else {
              inputElement = document.createElement("input");
              inputElement.type = "text";
              inputElement.value = val ?? "";
            }

            inputElement.name = key;
            inputElement.disabled = true;
            inputElement.style.width = "100%";
            inputElement.style.border = "none";
            inputElement.style.background = "transparent";
            inputElement.style.outline = "none";

            // Mark mandatory fields visually
            if (mandatoryKeys.includes(key)) {
              inputElement.required = true;
              inputElement.dataset.mandatory = "true";
            }

            // When user types in edit mode, dynamically show red border if empty
            inputElement.addEventListener("input", () => {
              if (inputElement.dataset.mandatory === "true") {
                inputElement.style.border = inputElement.value.trim()
                  ? "1px solid #ccc"
                  : "2px solid red";
              }
            });

            tr.innerHTML = `<td style="padding:4px; border:1px solid #ccc;">
              <b>${key}${
                        mandatoryKeys.includes(key)
                          ? " <span style='color:red'>*</span>"
                          : ""
                      }</b>
            </td>`;

            const tdValue = document.createElement("td");
            tdValue.style.padding = "4px";
            tdValue.style.border = "1px solid #ccc";

            // tdValue.appendChild(inputElement);

            tdValue.appendChild(inputElement);

// --- Add small "Other" input field logic (for Foot_Path, Cons_Mat, FP_Cons_Ma, Sub_Class) ---
if (["Foot_Path", "Cons_Mat", "FP_Cons_Ma", "Sub_Class"].includes(key)) {
  const otherInput = document.createElement("input");
  otherInput.type = "text";
  otherInput.placeholder = "Specify other...";
  otherInput.style.display = "none";
  otherInput.style.marginTop = "4px";
  otherInput.style.width = "100%";
  otherInput.name = `${key}_other`;
  otherInput.disabled = true; // stays disabled until Edit mode is on

  // If existing value isn't in the predefined list, show the field prefilled
  const allowedValues = Array.from(inputElement.options).map(o => o.value);
  if (!allowedValues.includes(val) && val) {
    inputElement.value = "Other";
    otherInput.value = val;
    otherInput.style.display = "block";
  }

  // Show/hide when user selects "Other"
  inputElement.addEventListener("change", () => {
    if (inputElement.value === "Other") {
      otherInput.style.display = "block";
    } else {
      otherInput.style.display = "none";
      otherInput.value = "";
    }
  });

  tdValue.appendChild(otherInput);
}


            tr.appendChild(tdValue);
            tbody.appendChild(tr);
          });

          //  Keep the property internally (do not display, but retain)
          [
            "sur_sts",
            "OBJECTID_1",
            "OBJECTID_2",
            "sur_sts",
            "OBJECTID_1",
            "OBJECTID_2",
            "Join_Count",
            "TARGET_FID",
            "OBJECTID",
            "Join_Cou_1",
            "TARGET_F_1",
            "osm_id",
            "name",
            "Length_km",
            "Ward_No",
            "Maintain",
            "villageNam",
            "Row_Width",
          ].forEach((hiddenKey) => {
            if (hiddenKey in props) {
              const hiddenInput = document.createElement("input");
              hiddenInput.type = "hidden";
              hiddenInput.name = hiddenKey;
              hiddenInput.value = props[hiddenKey];
              table.appendChild(hiddenInput);
            }
          });

          table.appendChild(tbody);
          popupDiv.appendChild(table);

          // const toggleCheckbox = headerDiv.querySelector("#toggleEditBtn");
          // const toggleTrack = headerDiv.querySelector("#toggleTrack");
          // const toggleThumb = headerDiv.querySelector("#toggleThumb");

          const saveFeatureData = async () => {
            const inputs = popupDiv.querySelectorAll("input[type='text'], select");
            const updatedData = {};
            inputs.forEach((inp) => (updatedData[inp.name] = inp.value));
            
            if ("OBJECTID_1" in feature.properties)
              updatedData.OBJECTID_1 = feature.properties.OBJECTID_1;
            if ("OBJECTID_2" in feature.properties)
              updatedData.OBJECTID_2 = feature.properties.OBJECTID_2;
            if ("OBJECTID" in feature.properties)
              updatedData.OBJECTID = feature.properties.OBJECTID;
            // if ("sur_sts" in feature.properties)
            //   updatedData.sur_sts = feature.properties.sur_sts;
            if ("TARGET_FID" in feature.properties)
              updatedData.TARGET_FID = feature.properties.TARGET_FID;
            if ("TARGET_F_1" in feature.properties)
              updatedData.TARGET_F_1 = feature.properties.TARGET_F_1;
            if ("Join_Cou_1" in feature.properties)
              updatedData.Join_Cou_1 = feature.properties.Join_Cou_1;
            if ("Join_Count" in feature.properties)
              updatedData.Join_Count = feature.properties.Join_Count;
            if ("osm_id" in feature.properties)
              updatedData.osm_id = feature.properties.osm_id;
            if ("name" in feature.properties)
              updatedData.name = feature.properties.name;
            if ("Length_km" in feature.properties)
              updatedData.Length_km = feature.properties.Length_km;
            if ("Ward_No" in feature.properties)
              updatedData.Ward_No = feature.properties.Ward_No;
            if ("Maintain" in feature.properties)
              updatedData.Maintain = feature.properties.Maintain;
            if ("villageNam" in feature.properties)
              updatedData.villageNam = feature.properties.villageNam;
            if ("Row_Width" in feature.properties)
              updatedData.Row_Width = feature.properties.Row_Width;

            // Always mark as surveyed
            updatedData.sur_sts = true;

            try {
              const response = await fetch(`${HOST}/update-feature-geojson`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({
                  updatedData: updatedData,
                  layerName: layerName,
                }),
              });

              if (!response.ok) throw new Error("Failed to update feature");

              console.log(" Auto-save success");
              Object.assign(feature.properties, updatedData);
              layer.setStyle({ color: "green" });
            } catch (err) {
              console.error("Error auto-saving:", err);
            }
          };

          // toggleCheckbox.addEventListener("change", () => {
          //   const isEditable = toggleCheckbox.checked;
          //   const inputs = popupDiv.querySelectorAll(
          //     "input[type='text'], select"
          //   );
          //   inputs.forEach((inp) => (inp.disabled = !isEditable));

          //   toggleTrack.style.backgroundColor = isEditable ? "#2b7a2b" : "#ccc";
          //   toggleThumb.style.transform = isEditable
          //     ? "translateX(20px)"
          //     : "translateX(0)";

          //   if (isEditable) {
          //     autoSaveInterval = setInterval(saveFeatureData, 2000);
          //   } else {
          //     if (autoSaveInterval) clearInterval(autoSaveInterval);
          //     saveFeatureData();
          //   }
          // });

          // --- JS logic ---
          const editSaveBtn = headerDiv.querySelector("#editSaveBtn");

          let editMode = false;
          let autoSaveInterval = null;

          editSaveBtn.addEventListener("click", () => {
            editMode = !editMode;
            const inputs = popupDiv.querySelectorAll(
              "input[type='text'], select"
            );

            if (editMode) {
              // Enable edit mode
              inputs.forEach((inp) => (inp.disabled = false));
              editSaveBtn.textContent = "Save";
              editSaveBtn.style.backgroundColor = "#007bff"; // blue for save mode

              // Start autosave only after validation passes
              autoSaveInterval = setInterval(() => {
                if (areMandatoryFieldsFilled()) {
                  saveFeatureData();
                }
              }, 2000);
            } else {
              // Save mode (button clicked again)
              if (!areMandatoryFieldsFilled()) {
                alert("Please fill all mandatory fields before saving.");
                editMode = true; // keep edit mode on
                return;
              }

              // Stop autosave and save once manually
              if (autoSaveInterval) clearInterval(autoSaveInterval);
              saveFeatureData();

              // Disable inputs
              inputs.forEach((inp) => (inp.disabled = true));
              editSaveBtn.textContent = "Edit";
              editSaveBtn.style.backgroundColor = "#2b7a2b";
            }
          });

          // --- Helper function for mandatory fields ---
          function areMandatoryFieldsFilled() {
            const mandatoryKeys = [
              "Rd_Name",
              "Cons_Mat",
              "CW_Width",
              "FP_Width",
              "FP_Cons_Ma",
            ];
            for (const key of mandatoryKeys) {
              const field = popupDiv.querySelector(`[name='${key}']`);
              if (!field || !field.value.trim()) {
                return false;
              }
            }
            return true;
          }
          layer.on("popupclose", () => {
            if (autoSaveInterval) clearInterval(autoSaveInterval);
            layer.setStyle({
              color: feature.properties.sur_sts ? "green" : "black",
            });
          });

          layer.bindPopup(popupDiv);
        },
      });

      layer.addTo(map);
      setPanvelLayer(layer);

      map.flyToBounds(layer.getBounds());
    } catch (err) {
      console.error("Error loading GeoJSON:", err);
    }
  };

  return (
    <>
      <div className="mt-4 scroll-sidebar-div">
        <div className=" hide-show-container">
          <div className="m-2 mt-3">
            <h6>Panvel Survey</h6>

            {/* <div className="opt-div">
              <input
                value="sam_div/Div1Roads/aaatrial.geojson"
                name="Panvelroads"
                id="sam_div/Div1Roads/aaatrial.geojson"
                // className="form-check-input check-map"
                type="radio"
                onChange={addPanvelLayer}
              />
              <label htmlFor="aaatrial">trial 1</label>
            </div> */}

          <div className="opt-div">
              <input
                value="sam_div/Div1Roads/aa_trial.geojson"
                name="Panvelroads"
                id="sam_div/Div1Roads/aa_trial.geojson"
                // className="form-check-input check-map"
                type="radio"
                onChange={addPanvelLayer}
              />
              <label htmlFor="aaatrial">trial 2</label>
            </div>
            
            {/* <div className="opt-div">
              <input
                value="sam_div/Div1Roads/trial_3.geojson"
                name="Panvelroads"
                id="sam_div/Div1Roads/trial_3.geojson"
                // className="form-check-input check-map"
                type="radio"
                onChange={addPanvelLayer}
              />
              <label htmlFor="aaatrial">trial 3</label>
            </div> */}
            <details id="townD">
              <summary className="townS">Division 1 Roads</summary>
              <div className="town-cont">
                <div className="opt-div">
                  <input
                    value="sam_div/Div1Roads/Adivali_PL.geojson"
                    name="Panvelroads"
                    id="Adivali_PL"
                    // className="form-check-input check-map"
                    type="radio"
                    onChange={addPanvelLayer}
                  />
                  <label htmlFor="Adivali_PL">Adivali_PL</label>
                </div>

                <div className="opt-div">
                  <input
                    value="sam_div/Div1Roads/Bid.geojson"
                    name="Panvelroads"
                    id="Bid"
                    // className="form-check-input check-map"
                    type="radio"
                    onChange={addPanvelLayer}
                  />
                  <label htmlFor="Bid">Bid</label>
                </div>

                <div className="opt-div">
                  <input
                    value="sam_div/Div1Roads/Dhansar.geojson"
                    id="Dhansar"
                    name="Panvelroads"
                    // className="form-check-input check-map"
                    type="radio"
                    onChange={addPanvelLayer}
                  />
                  <label htmlFor="Dhansar">Dhansar</label>
                </div>

                <div className="opt-div">
                  <input
                    value="sam_div/Div1Roads/Ghot.geojson"
                    id="Ghot"
                    // className="form-check-input check-map"
                    type="radio"
                    name="Panvelroads"
                    onChange={addPanvelLayer}
                  />
                  <label htmlFor="Ghot">Ghot</label>
                </div>

                <div className="opt-div">
                  <input
                    value="sam_div/Div1Roads/Karavale Bk..geojson"
                    id="Karavale_Bk"
                    // className="form-check-input check-map"
                    type="radio"
                    name="Panvelroads"
                    onChange={addPanvelLayer}
                  />
                  <label htmlFor="Karavale_Bk">Karavale Bk.</label>
                </div>

                <div className="opt-div">
                  <input
                    value="sam_div/Div1Roads/KhargharCT.geojson"
                    id="Kharghar_CT"
                    // className="form-check-input check-map"
                    type="radio"
                    name="Panvelroads"
                    onChange={addPanvelLayer}
                  />
                  <label htmlFor="Kharghar_CT">Kharghar (C.T.)</label>
                </div>

                <div className="opt-div">
                  <input
                    value="sam_div/Div1Roads/Koyana Velhe.geojson"
                    id="Koyana_Velhe"
                    // className="form-check-input check-map"
                    type="radio"
                    name="Panvelroads"
                    onChange={addPanvelLayer}
                  />
                  <label htmlFor="Koyana_Velhe">Koyana Velhe</label>
                </div>

                {/* <div className="opt-div">
                <input
                  value="sam_div/Div1Roads/NULL.geojson"
                  id="NULL"
                  // className="form-check-input check-map"
                  type="radio"  name="Panvelroads"
                  onChange={addPanvelLayer}
                />
                <label htmlFor="NULL">NULL</label>
              </div> */}

                <div className="opt-div">
                  <input
                    value="sam_div/Div1Roads/Owe.geojson"
                    id="Owe"
                    // className="form-check-input check-map"
                    type="radio"
                    name="Panvelroads"
                    onChange={addPanvelLayer}
                  />
                  <label htmlFor="Owe">Owe</label>
                </div>

                <div className="opt-div">
                  <input
                    value="sam_div/Div1Roads/Pendhar.geojson"
                    id="Pendhar"
                    // className="form-check-input check-map"
                    type="radio"
                    name="Panvelroads"
                    onChange={addPanvelLayer}
                  />
                  <label htmlFor="Pendhar">Pendhar</label>
                </div>

                <div className="opt-div">
                  <input
                    value="sam_div/Div1Roads/Pisarve.geojson"
                    id="Pisarve"
                    // className="form-check-input check-map"
                    type="radio"
                    name="Panvelroads"
                    onChange={addPanvelLayer}
                  />
                  <label htmlFor="Pisarve">Pisarve</label>
                </div>

                <div className="opt-div">
                  <input
                    value="sam_div/Div1Roads/Rohinjan.geojson"
                    id="Rohinjan"
                    // className="form-check-input check-map"
                    type="radio"
                    name="Panvelroads"
                    onChange={addPanvelLayer}
                  />
                  <label htmlFor="Rohinjan">Rohinjan</label>
                </div>

                <div className="opt-div">
                  <input
                    value="sam_div/Div1Roads/TalodePanchnadCT.geojson"
                    id="Talode_Panchnad_CT"
                    // className="form-check-input check-map"
                    type="radio"
                    name="Panvelroads"
                    onChange={addPanvelLayer}
                  />
                  <label htmlFor="Talode_Panchnad_CT">
                    Talode Panchnad (C.T.)
                  </label>
                </div>

                <div className="opt-div">
                  <input
                    value="sam_div/Div1Roads/Taloje Majkur.geojson"
                    id="Taloje_Majkur"
                    // className="form-check-input check-map"
                    type="radio"
                    name="Panvelroads"
                    onChange={addPanvelLayer}
                  />
                  <label htmlFor="Taloje_Majkur">Taloje Majkur</label>
                </div>

                {/* <div className="opt-div">
                  <input
                    value="sam_div/Div1Roads/Tondhare.geojson"
                    id="Tondhare"
                    // className="form-check-input check-map"
                    type="radio"
                    name="Panvelroads"
                    onChange={addPanvelLayer}
                  />
                  <label htmlFor="Tondhare">Tondhare</label>
                </div> */}

                <div className="opt-div">
                  <input
                    value="sam_div/Div1Roads/Turbhe.geojson"
                    id="Turbhe"
                    // className="form-check-input check-map"
                    type="radio"
                    name="Panvelroads"
                    onChange={addPanvelLayer}
                  />
                  <label htmlFor="Turbhe">Turbhe</label>
                </div>
              </div>
            </details>

            <details id="townD">
              <summary className="townS">Division 2 Roads</summary>
              <div className="town-cont">
                <div className="opt-div">
                  <input
                    value="sam_div/Div2Roads/Chal.geojson"
                    id="Chal"
                    // className="form-check-input check-map"
                    type="radio"
                    name="Panvelroads"
                    onChange={addPanvelLayer}
                  />
                  <label>Chal</label>
                </div>

                <div className="opt-div">
                  <input
                    value="sam_div/Div2Roads/Asudgoan.geojson"
                    id="Asudgoan"
                    // className="form-check-input check-map"
                    type="radio"
                    name="Panvelroads"
                    onChange={addPanvelLayer}
                  />
                  <label>Asudgoan</label>
                </div>

                <div className="opt-div">
                  <input
                    value="sam_div/Div2Roads/Devichapada.geojson"
                    id="Devichapada"
                    // className="form-check-input check-map"
                    type="radio"
                    name="Panvelroads"
                    onChange={addPanvelLayer}
                  />
                  <label>Devichapada</label>
                </div>

                {/* <div className="opt-div">
                  <input
                    value="sam_div/Div2Roads/Dhansar.geojson"
                    id="Dhansar"
                    // className="form-check-input check-map"
                    type="radio"
                    name="Panvelroads"
                    onChange={addPanvelLayer}
                  />
                  <label>Dhansar</label>
                </div> */}

                {/* <div className="opt-div">
                  <input
                    value="sam_div/Div2Roads/Ghot.geojson"
                    id="Ghot"
                    // className="form-check-input check-map"
                    type="radio"
                    name="Panvelroads"
                    onChange={addPanvelLayer}
                  />
                  <label>Ghot</label>
                </div> */}

                <div className="opt-div">
                  <input
                    value="sam_div/Div2Roads/Kalamboli.geojson"
                    id="Kalamboli"
                    // className="form-check-input check-map"
                    type="radio"
                    name="Panvelroads"
                    onChange={addPanvelLayer}
                  />
                  <label>Kalamboli</label>
                </div>

                {/* <div className="opt-div">
                  <input
                    value="sam_div/Div2Roads/Kamothe.geojson"
                    id="Kamothe"
                    // className="form-check-input check-map"
                    type="radio"
                    name="Panvelroads"
                    onChange={addPanvelLayer}
                  />
                  <label>Kamothe</label>
                </div> */}

                <div className="opt-div">
                  <input
                    value="sam_div/Div2Roads/Karavale Bk..geojson"
                    id="Karavale Bk."
                    // className="form-check-input check-map"
                    type="radio"
                    name="Panvelroads"
                    onChange={addPanvelLayer}
                  />
                  <label>Karavale Bk.</label>
                </div>

                {/* <div className="opt-div">
                  <input
                    value="sam_div/Div2Roads/Kharghar (C.T.).geojson"
                    id="Kharghar (C.T.)"
                    // className="form-check-input check-map"
                    type="radio"
                    name="Panvelroads"
                    onChange={addPanvelLayer}
                  />
                  <label>Kharghar (C.T.)</label>
                </div> */}

                <div className="opt-div">
                  <input
                    value="sam_div/Div2Roads/Khidukpada.geojson"
                    id="Khidukpada"
                    // className="form-check-input check-map"
                    type="radio"
                    name="Panvelroads"
                    onChange={addPanvelLayer}
                  />
                  <label>Khidukpada</label>
                </div>
{/* 
                <div className="opt-div">
                  <input
                    value="sam_div/Div2Roads/Koyana Velhe.geojson"
                    id="Koyana Velhe"
                    // className="form-check-input check-map"
                    type="radio"
                    name="Panvelroads"
                    onChange={addPanvelLayer}
                  />
                  <label>Koyana Velhe</label>
                </div> */}

                <div className="opt-div">
                  <input
                    value="sam_div/Div2Roads/Nagzari.geojson"
                    id="Nagzari"
                    // className="form-check-input check-map"
                    type="radio"
                    name="Panvelroads"
                    onChange={addPanvelLayer}
                  />
                  <label>Nagzari</label>
                </div>

                <div className="opt-div">
                  <input
                    value="sam_div/Div2Roads/Nawade.geojson"
                    id="Nawade"
                    // className="form-check-input check-map"
                    type="radio"
                    name="Panvelroads"
                    onChange={addPanvelLayer}
                  />
                  <label>Nawade</label>
                </div>

                {/* <div className="opt-div">
      // <input value="sam_div/Div2Roads/NULL.geojson" id="NULL" className="form-check-input check-map" type="radio"  name="Panvelroads"  onChange={addPanvelLayer} />
      <label>NULL</label>
    </div> */}

                <div className="opt-div">
                  <input
                    value="sam_div/Div2Roads/Padagha.geojson"
                    id="Padagha"
                    // className="form-check-input check-map"
                    type="radio"
                    name="Panvelroads"
                    onChange={addPanvelLayer}
                  />
                  <label>Padagha</label>
                </div>

                <div className="opt-div">
                  <input
                    value="sam_div/Div2Roads/palekhurd.geojson"
                    id="palekhurd"
                    // className="form-check-input check-map"
                    type="radio"
                    name="Panvelroads"
                    onChange={addPanvelLayer}
                  />
                  <label>Palekhurd</label>
                </div>

                <div className="opt-div">
                  <input
                    value="sam_div/Div2Roads/Panvel_map.geojson"
                    id="Panvel_map"
                    // className="form-check-input check-map"
                    type="radio"
                    name="Panvelroads"
                    onChange={addPanvelLayer}
                  />
                  <label>Panvel_map</label>
                </div>

                <div className="opt-div">
                  <input
                    value="sam_div/Div2Roads/Pendhar.geojson"
                    id="Pendhar"
                    // className="form-check-input check-map"
                    type="radio"
                    name="Panvelroads"
                    onChange={addPanvelLayer}
                  />
                  <label>Pendhar</label>
                </div>

                {/* <div className="opt-div">
                  <input
                    value="sam_div/Div2Roads/Pisarve.geojson"
                    id="Pisarve"
                    // className="form-check-input check-map"
                    type="radio"
                    name="Panvelroads"
                    onChange={addPanvelLayer}
                  />
                  <label>Pisarve</label>
                </div> */}

                <div className="opt-div">
                  <input
                    value="sam_div/Div2Roads/Rodpali.geojson"
                    id="Rodpali"
                    // className="form-check-input check-map"
                    type="radio"
                    name="Panvelroads"
                    onChange={addPanvelLayer}
                  />
                  <label>Rodpali</label>
                </div>

                {/* <div className="opt-div">
                  <input
                    value="sam_div/Div2Roads/Rohinjan.geojson"
                    id="Rohinjan"
                    // className="form-check-input check-map"
                    type="radio"
                    name="Panvelroads"
                    onChange={addPanvelLayer}
                  />
                  <label>Rohinjan</label>
                </div> */}

                {/* <div className="opt-div">
                  <input
                    value="sam_div/Div2Roads/Talode Panchnad (C.T.).geojson"
                    id="Talode Panchnad (C.T.)"
                    // className="form-check-input check-map"
                    type="radio"
                    name="Panvelroads"
                    onChange={addPanvelLayer}
                  />
                  <label>Talode Panchnad (C.T.)</label>
                </div> */}

                {/* <div className="opt-div">
                  <input
                    value="sam_div/Div2Roads/Taloje Majkur.geojson"
                    id="Taloje Majkur"
                    // className="form-check-input check-map"
                    type="radio"
                    name="Panvelroads"
                    onChange={addPanvelLayer}
                  />
                  <label>Taloje Majkur</label>
                </div> */}

                <div className="opt-div">
                  <input
                    value="sam_div/Div2Roads/Tembhode.geojson"
                    id="Tembhode"
                    // className="form-check-input check-map"
                    type="radio"
                    name="Panvelroads"
                    onChange={addPanvelLayer}
                  />
                  <label>Tembhode</label>
                </div>

                <div className="opt-div">
                  <input
                    value="sam_div/Div2Roads/Tondhare.geojson"
                    id="Tondhare"
                    // className="form-check-input check-map"
                    type="radio"
                    name="Panvelroads"
                    onChange={addPanvelLayer}
                  />
                  <label>Tondhare</label>
                </div>

                {/* <div className="opt-div">
                  <input
                    value="sam_div/Div2Roads/Turbhe.geojson"
                    id="Turbhe"
                    // className="form-check-input check-map"
                    type="radio"
                    name="Panvelroads"
                    onChange={addPanvelLayer}
                  />
                  <label>Turbhe</label>
                </div> */}

                <div className="opt-div">
                  <input
                    value="sam_div/Div2Roads/Walawali.geojson"
                    id="Walawali"
                    // className="form-check-input check-map"
                    type="radio"
                    name="Panvelroads"
                    onChange={addPanvelLayer}
                  />
                  <label>Walawali</label>
                </div>
              </div>
            </details>

            <details id="townD">
              <summary className="townS">Division 3 Roads</summary>
              <div className="town-cont">
                <div className="opt-div">
                  <input
                    value="sam_div/Div3Roads/Kalamboli.geojson"
                    id="Nov 2019"
                    // className="form-check-input check-map"
                    type="radio"
                    name="Panvelroads"
                    onChange={addPanvelLayer}
                  />
                  <label>Kalamboli</label>
                </div>

                <div className="opt-div">
                  <input
                    value="sam_div/Div3Roads/Kamothe.geojson"
                    id="May 2020"
                    // className="form-check-input check-map"
                    type="radio"
                    name="Panvelroads"
                    onChange={addPanvelLayer}
                  />
                  <label>Kamothe</label>
                </div>

                <div className="opt-div">
                  <input
                    value="sam_div/Div3Roads/Panvel_map.geojson"
                    id="Nov 2020"
                    // className="form-check-input check-map"
                    type="radio"
                    name="Panvelroads"
                    onChange={addPanvelLayer}
                  />
                  <label>Panvel_map</label>
                </div>
              </div>
            </details>

            <details id="townD">
              <summary className="townS">Division 4 Roads</summary>
              <div className="town-cont">
                <div className="opt-div">
                  <input
                    value="sam_div/Div4Roads/Kalundre (C.T.).geojson"
                    id="May 2021"
                    // className="form-check-input check-map"
                    type="radio"
                    name="Panvelroads"
                    onChange={addPanvelLayer}
                  />
                  <label>Kalundre (C.T.)</label>
                </div>

                <div className="opt-div">
                  <input
                    value="sam_div/Div4Roads/Panvel_map.geojson"
                    id="Nov 2021"
                    // className="form-check-input check-map"
                    type="radio"
                    name="Panvelroads"
                    onChange={addPanvelLayer}
                  />
                  <label>Panvel_map</label>
                </div>
              </div>
            </details>
          </div>
        </div>
      </div>
    </>
  );
}
